package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 기간 출결 집계. `attendanceRecords`를 기간으로 읽어 클라이언트에서 접은 결과다.
 *
 * **집계 단위는 (학생 × 수업일) 1건**이다. 04:00 스케줄러가 등원하지 않은 학생의
 * `NOT_ARRIVED` 행까지 미리 깔아 두므로([AttendanceSource.ROSTER]) 분모가 빠지지 않는다 —
 * 이벤트만 세면 "오지 않은 날"이 통계에서 사라진다.
 *
 * **지각은 축이 둘이다.** [lateCount]는 "지금 상태가 `LATE`인 건수"로, 상호배타 5개 상태 중
 * 하나다 — 지각 등원 후 하원하면 `LEFT`로 접혀 여기서 빠진다. 기간 전체의 지각 횟수는
 * [lateArrivalCount]로 센다: 서버가 등원 시점의 지각 여부를 `status`와 별개로
 * [DailyRosterEntry.lateArrival]에 남겨 하원 후에도 유지한다.
 */
@Serializable
data class AttendanceStats(
    /** 포함 구간 `yyyy-MM-dd`. */
    val startDate: String,
    val endDate: String,
    val presentCount: Int = 0,
    val lateCount: Int = 0,
    val absentCount: Int = 0,
    val leftCount: Int = 0,
    val notArrivedCount: Int = 0,
    /**
     * 지각으로 등원한 건수. 상호배타 5개 상태와 **겹치는 별도 축**이라
     * [totalCount]·[attendedCount]에 더하지 않는다 — 지각 등원 후 하원한 건은
     * [leftCount]와 이 값에 동시에 잡히는 것이 정상이다.
     */
    val lateArrivalCount: Int = 0,
) {
    /** 집계 대상 총 건수. */
    val totalCount: Int
        get() = presentCount + lateCount + absentCount + leftCount + notArrivedCount

    /** 등원으로 인정하는 건수 — 지각도 하원도 "왔다"는 사실은 같다. */
    val attendedCount: Int get() = presentCount + lateCount + leftCount

    /**
     * 0.0~1.0. 집계 대상이 하나도 없으면 null이다 —
     * "출석률 0%"와 "데이터 없음"은 화면에서 다르게 보여야 한다.
     */
    val attendanceRate: Double? get() = if (totalCount == 0) null else attendedCount.toDouble() / totalCount

    /**
     * 등원한 건 중 지각 비율(0.0~1.0). 분모는 [attendedCount]다 — 오지 않은 날은 지각할 기회가
     * 없으므로 [totalCount]로 나누면 지각률이 결석률에 희석된다.
     *
     * 분모가 0이면 null이다([attendanceRate]와 같은 규약 — "지각률 0%"와 "데이터 없음"은 다르다).
     */
    val lateRate: Double? get() = if (attendedCount == 0) null else lateArrivalCount.toDouble() / attendedCount
}

/** 날짜별 한 칸. 추이 그래프와 월별 캘린더의 원본이다. */
@Serializable
data class DailyAttendanceStat(
    val serviceDate: String,
    val stats: AttendanceStats,
)

/** 학생별 한 줄. 스태프 통계 화면의 표 한 행이다. */
@Serializable
data class StudentAttendanceStat(
    val studentId: String,
    val studentName: String,
    val stats: AttendanceStats,
)

/** 반별 한 줄. 반 사이 비교용이다. */
@Serializable
data class ClassAttendanceStat(
    val classId: String,
    val className: String,
    val stats: AttendanceStats,
)

/**
 * 학원 통계 화면 한 벌. [GetAttendanceStatsUseCase]가 기간 원장 한 번 읽기로 전부 만든다.
 *
 * **스태프 전용이다.** rules가 `attendanceRecords` read를 ADMIN(학원 전체)과
 * 배정 반을 가진 EDITOR에게만 연다 — 학생·보호자는 이 경로를 읽을 수 없고
 * 자기 미러([AttendanceTimelineEntry])로만 본다(§4.3, §4.5).
 */
@Serializable
data class AcademyAttendanceStats(
    val academyId: String,
    val overall: AttendanceStats,
    val byDate: List<DailyAttendanceStat> = emptyList(),
    val byClass: List<ClassAttendanceStat> = emptyList(),
    val byStudent: List<StudentAttendanceStat> = emptyList(),
)

/**
 * 학생 개인 통계.
 *
 * 출처가 두 갈래라 [fromRoster]로 구분한다 — 스태프가 볼 때는 기간 원장(분모 포함, 출석률 유효),
 * 학생·보호자 본인이 볼 때는 개인 타임라인 미러(등원 이벤트만 존재)다.
 * 후자는 "오지 않은 날"이 애초에 데이터에 없어 [AttendanceStats.attendanceRate]가
 * 항상 1.0에 가깝게 나오므로 **화면에 출석률을 띄우면 안 된다**.
 */
@Serializable
data class StudentStatsView(
    val studentId: String,
    val studentName: String,
    val stats: AttendanceStats,
    val byDate: List<DailyAttendanceStat> = emptyList(),
    /** true면 기간 원장 기반(출석률 유효), false면 타임라인 미러 기반(횟수만 유효). */
    val fromRoster: Boolean,
) {
    /** 화면이 출석률을 표시해도 되는가. */
    val hasValidRate: Boolean get() = fromRoster && stats.totalCount > 0
}
